import * as React from "react";
import CGReactApplication from "./cgreact-application";
import CGReactPageStyle from "./cgreact-page.css";
import CGReactModalStyle from "./cgreact-modal.css";
import {copyObject, generateUUID} from "./cgreact-util";
import {LoadingState, PageProp, PageState, ToastConfig, ToastState} from "../types/cgreact-type-page";
import {ModalState} from "../types/cgreact-type-modal";
import {CGReactPageException} from "./cgreact-exception";

/**
 * 陈光封装的React对象，抽象一个页面Page，所有组件状态提升到CGReact的Page中
 * 在不破坏类型提升思想的条件下，去除了state的readonly属性，使得用户可以自由修改
 * 同时，通过Proxy动态代理技术，为state中的每一个键值对都注入刷新页面的方法
 * 用户通过this.state.xxx=vvv 赋值的时候，就会自动触发界面刷新，使用更加自然，更加方便
 */
export default abstract class CGReactPage<STATE extends PageState = PageState, PROP extends PageProp = PageProp> extends React.Component<PROP, STATE>
{
    /** 使用ContentProvider机制提供本类实例给子标签*/
    public static ParentPageContext = React.createContext<CGReactPage>(null);//定义ParentPageContext
    /** CGReact组件类型定义*/
    public static contextType = CGReactApplication.ApplicationContext; //自动注入this.context=ApplicationContext
    public static CGReactModuleType = "CGReactPage"; //CGReactPage及其子类都得到相同的值
    public static RecentClassInstance: any; //保存本类或者子类的最新的一个实例
    /** 全局APP上下文*/
    public context: CGReactApplication;// 应用全局上下文对象
    public readonly state: STATE; //继承一下state就消除了它的ReadOnly属性,再render中会将state处理为一个Proxy对象
    public state_original: STATE; //为了避免把Proxy对象传递给子组件（子组件可能再渲染过程中改变prop)导致重复调用setState报错，记录一下真实的state数据
    /** 页面样式的CSSModule类*/
    public JSSStyle: { [key in string]: JSS }; //通过affectStyle注解生效的所有JSS样式
    public CSSStyle: { [key in string]: string }; //通过affectStyle注解生效的所有CSS样式
    public SCSSStyle: { [key in string]: string }; //通过affectStyle注解生效的所有SCSS样式
    public style = {};
    /** 页面需要渲染的吐司和对话框*/
    private PageErrorState: CGReactPageException;
    private PageToastState: Array<ToastConfig> = [];
    private PageDialogState: ModalState;
    private PageDialogRef: React.RefObject<HTMLDivElement> = React.createRef<HTMLDivElement>();
    private PageLoadingState: LoadingState;
    private PageLoadingRef: React.RefObject<HTMLDivElement> = React.createRef<HTMLDivElement>();
    /** 页面标识位*/
    private StateFlag = true;//为了避免异步代码空间中多次赋值导致的页面重复刷新多次，定义一个变量只让第一次生效:参考：this.refresh()
    private MountOK = false;//为了在原生componentUpdate中区别是首次加载好还是页面更新设计一个标识位，在stateReady时置为true
    /** 构造函数，prop无需传入任何参数，用state来控制*/
    constructor(props: PROP)
    {
        super(props);
        // @ts-ignore 此类和继承此类的所有子类，保存最后一个类实例
        this.constructor.RecentClassInstance = this;
    }

    /** 绑定界面加载回调*/
    componentDidMount(): void
    {
        // 开始执行页面数据生命周期
        this.beforeInit().then(() =>
        {
            // 页面请求没有错误，开始构造数据
            this.overrideState();
        }).catch((error: CGReactPageException) =>
        {
            // 直接加载错误页面
            this.PageErrorState = error;
            this.refresh();
        });
        // 生效基础page样式
        this.context.importAssetsSCSS("CGReactPage", CGReactPageStyle as any, this.context.FrameStyleAnchor);
        this.context.importAssetsSCSS("CGReactModal", CGReactModalStyle as any, this.context.FrameStyleAnchor);
        // 自动绑定全局插槽
        typeof this["onWindowResize"] == "function" && (this.context.WindowResizeSignal.bind(this as any));
        typeof this["onKeyDown"] == "function" && (this.context.KeyboardPressSignal.bind(this as any));
    }

    componentDidUpdate(prevProps: Readonly<PROP>, prevState: Readonly<STATE>, snapshot?: any): void
    {
        if (this.state_original)
        {
            if (this.MountOK)
            {
                // 生效引入外部样式
                this.context.affectSpecificStyle(this.JSSStyle, this.CSSStyle, this.SCSSStyle);
                // 调用用户代码空间回调函数
                this.onPageDidMount();
                this.MountOK = false;
            }
            // 自动调整对话框如果有的话
            if (this.PageDialogRef.current)
            {
                let left = (window.innerWidth - this.PageDialogRef.current.offsetWidth) / 2;
                let top = (window.innerHeight - this.PageDialogRef.current.offsetHeight) / 2;
                this.PageDialogRef.current.style.top = top + "px";
                this.PageDialogRef.current.style.left = left + "px";
            }
            // 用户空间刷新回调
            this.onPageDidUpdate();
        }
    }

    /** 绑定界面卸载回调*/
    componentWillUnmount(): void
    {
        // 清理所有托管的JS和CSS
        // for (let i = 0; i < this.CssAndJSList.length; i++)
        // {
        //     this.CssAndJSList[i].invalid();
        // }
        // 用户空间回调
        this.onPageWillUnMount();
        // 自动解除全局插槽
        typeof this["onWindowResize"] == "function" && (this.context.WindowResizeSignal.unbind(this as any));
        typeof this["onKeyDown"] == "function" && (this.context.KeyboardPressSignal.unbind(this as any));
    }

    /** 初始化之前判定是否能够加载此页面*/
    protected async beforeInit(): Promise<void>
    {
    }

    /** 初始化用户数据*/
    protected abstract async initState(): Promise<STATE>;

    /** 初始化用户界面*/
    protected abstract renderView(): JSX

    /** 数据初始化完成之前的加载中页面*/
    protected renderLoadingView(): JSX
    {
        return undefined;
    }

    /** 页面遇到错误展示的报错页面*/
    protected renderErrorView(error: CGReactPageException): JSX
    {
        return undefined;
    }

    /** 继承setState 防止用户调用*/
    setState(state): void
    {
        console.warn("您的调用无效，CGReact中直接数据赋值驱动，无需调用setState");
    }

    /** 重新进行页面数据初始化*/
    private overrideState(): void
    {
        this.initState().then((state: STATE) =>
        {
            // @ts-ignore 用户有时不需要页面数据会return undefine也要能正确处理
            this.state_original = state ? state : {};
            this.makeState(this.state_original);
            // 标识位配置为true
            this.MountOK = true;
            // 用户回调
            this.onPageStateReady();
            // 重新渲染页面
            this.refresh();
        });
    }

    /** 改变state的方法，避免用户调用*/
    private makeState(newState): void
    {
        // @ts-ignore
        this.state = newState;
    }

    /** 递归的使用ES7的动态代理Proxy技术，为state中的所有object注入监听函数，做到数据赋值的自动刷新*/
    private makeStateProxy<T>(obj: T): T
    {
        // 判断是否为object或者array
        if (typeof obj != "object" || obj instanceof Date)
        {
            return obj;
        }
        let proxy: any = obj instanceof Array ? [] : {};
        // 继续递归拷贝
        for (let key in obj)
        {
            proxy[key] = this.makeStateProxy(obj[key]);
        }
        // 注入监听方法
        proxy = new Proxy(proxy, {
            set: (target, key, value, receiver): boolean =>
            {
                // console.log("." + key.toString() + "=" + value);
                /**
                 * 为了防止用户使用state自我拷贝导致state_original变为代理的问题
                 * 比如：this.state.obj1=this.state.obj2  (此时obj2是一个Proxy对象）所以
                 * value会拿到代理对象值，从而把obj[key]（其实就是state_original)变成代理
                 * 我们需要把代理对象还原为普通对象
                 */
                obj[key] = copyObject(value);
                /**
                 * 为了防止用户将state中的对象从undefine改为object并后续赋值导致的报错
                 * 比如： this.state.obj1={}  (obj1原来是undefine，当然也没有代理对象）
                 * 然后 this.state.xx=111 会报错，因为代理对象的xx仍然是undefine
                 * 因此需要让代理同步这个新注入的对象
                 */
                target[key] = this.makeStateProxy(obj[key]);
                // 刷新页面
                this.refresh();
                return true;
            }
        });
        return proxy;
    }

    /** 绑定react原生渲染界面函数*/
    render(): React.ReactElement
    {
        if (this.PageErrorState)
        {
            // 出现了渲染前页面错误
            let errorView = this.renderErrorView(this.PageErrorState);
            errorView = errorView ? errorView : this.context.renderPageDefaultErrorView(this.PageErrorState);
            return errorView;
        }
        // 页面状态正常可以渲染
        if (!this.state_original)
        {
            // 渲染加载中页面，优先使用本页面配置
            let loadingView = this.renderLoadingView();
            loadingView = loadingView ? loadingView : this.context.renderPageDefaultLoadingView();
            return loadingView;
        } else
        {
            /**
             * 这样做主要有2个原因：
             * 1.setState的方式是克隆，因此setState方法会将原来的state=props.data
             * 变成props.data的克隆，这样state就不在是props（也就是父页面数据）的一个片段了
             * 因此这里需要还原一下，否则当父改变数据是，子组件中使用this.state初始化的内容都不会刷新
             * 2.在用户initView中不宜使用代理对象，如果将Proxy代理传给子组件及有可能导致报错
             * 因此这里将其还原为状态提升的state_original(它会是父的一个片段，并且是普通数据）
             * 这样既不会报错，也保障了状态提升能一直传递给子组件
             */
            this.makeState(this.state_original);
            /**
             * this.state已经被替换为普通的this.state_original
             * 从而避免了用户将Proxy对象通过prop传递给子组件时导致的报错*/
            let content = this.renderView();
            /**
             * 界面渲染完成，继续将this.state变为代理对象，监听用户赋值操作
             * 注意这里用的setState是改造过的，只改数据不刷新页面
             * 如果想刷新页面要调用super.makeState
             */
            this.makeState(this.makeStateProxy(this.state_original));
            // 开始渲染页面
            return <CGReactPage.ParentPageContext.Provider value={this}>
                {/*渲染页面内容元素*/}
                {content}
                {/*渲染页面吐司*/}
                {this.PageToastState.length > 0 && pageToast.call(this, this.PageToastState)}
                {/*渲染页面模态框*/}
                {this.PageDialogState && pageDialog.call(this, this.PageDialogState)}
                {/*渲染页面阻塞等待框*/}
                {this.PageLoadingState && pageLoadingModal.call(this, this.PageLoadingState)}
            </CGReactPage.ParentPageContext.Provider>;
        }

        function pageLoadingModal(config: LoadingState)
        {
            if (typeof config.last == "number")
            {
                // 100毫秒后启动计时
                window.setTimeout(() =>
                {
                    //last时间之后让对话框淡出
                    window.setTimeout(() =>
                    {
                        // 淡出
                        this.PageLoadingRef.current.className += ' layer-anim-close';
                        // 600毫秒后干掉对话框
                        this.PageLoadingState = null;
                        window.setTimeout(() => this.refresh(), 600);
                    }, config.last as number);
                }, 100);
            }
            return <div ref={this.PageLoadingRef} className="layui-layer layui-layer-loading jq-toast-fadein">
                <div className="layui-layer-content layui-layer-padding">
                    {/*渲染点点对话框图标*/}
                    {config.type == "dot" && <i className="layui-layer-ico layui-layer-loading0"></i>}
                    {/*渲染转圈对话框图标*/}
                    {config.type == "circle" && <React.Fragment>
						<i className="layui-layer-ico layui-layer-loading1"></i>
						<span>&nbsp;Loading &nbsp;.......</span>
					</React.Fragment>}
                </div>
                {/*渲染阻塞框遮罩*/}
                <div className="layui-layer-shade"></div>
            </div>;
        }

        function pageToast(config: Array<ToastConfig>)
        {
            return <div className="jq-toast-wrap">
                {config.map((curToast, index) => line.call(this, curToast, index))}
            </div>;

            function line(line: ToastConfig, index: number)
            {
                if (!line.proref)
                {
                    // 如果没有构造就启动进度条动画
                    let progressBarRef = React.createRef<HTMLSpanElement>();
                    window.setTimeout(() =>
                    {
                        // 100毫秒后启动进度条动画
                        progressBarRef.current.style.width = "0%";
                        window.setTimeout(() =>
                        {
                            // lastTime之后让吐司淡出
                            progressBarRef.current.parentElement.className += " jq-toast-fadeout";
                            window.setTimeout(() =>
                            {
                                //600毫秒后刷新页面干掉吐司，但不要刷新页面
                                this.PageToastState.splice(this.PageToastState.indexOf(line), 1);
                                progressBarRef.current.parentElement.style.display = "none";
                            }, 600);
                        }, line.last);
                    }, 100);
                    line.proref = progressBarRef;
                }
                return <div key={line.key} className={makeToastClass.call(this, line)}>
                    {/*渲染吐司进度条*/}
                    <span ref={line.proref} className="jq-toast-loader" style={{
                        transition: " width " + line.last / 1000 + "s ease-in",
                        OTransition: "width " + line.last / 1000 + "s ease-in",
                        WebkitTransition: "width  " + line.last / 1000 + "s ease-in",
                    }}></span>
                    {/*渲染吐司关闭按钮*/}
                    <span className="close-jq-toast-single">×</span>
                    {/*渲染吐司标题*/}
                    <h2 className="jq-toast-heading">{line.title}</h2>
                    {/*渲染吐司信息*/}
                    {line.message}
                </div>;
            }

            function makeToastClass(line: ToastState): string
            {
                let toastClass = ["jq-toast-single", "jq-has-icon", "jq-toast-fadein"];
                line.level == "info" && toastClass.push("jq-icon-info");
                line.level == "warn" && toastClass.push("jq-icon-warning");
                line.level == "success" && toastClass.push("jq-icon-success");
                line.level == "error" && toastClass.push("jq-icon-error");
                return toastClass.join(" ");
            }
        }

        function pageDialog(config: ModalState)
        {
            return <React.Fragment>
                {/*渲染对话框*/}
                <div ref={this.PageDialogRef} className="layui-layer layui-layer-dialog layer-anim-bounceIn">
                    {/*渲染对话框标题*/}
                    {config.title && <div className="layui-layer-title">{config.title}</div>}
                    {/*渲染对话框关闭按钮*/}
                    <div className="layui-layer-setwin" onClick={onCancelButtonClick.bind(this)}>
                        <a className="layui-layer-ico layui-layer-close layui-layer-close1"></a>
                    </div>
                    {/*对话框拖动区域*/}
                    <div className="layui-layer-drag"></div>
                    {/*渲染对话框内容*/}
                    <div className="layui-layer-content">
                        {/*首先渲染配置的元素*/}
                        {config.content}
                    </div>
                    {/*渲染对话框按钮*/}
                    {(config.cancel || config.confirm) && <div className="layui-layer-btn">
                        {config.confirm && <a className="layui-layer-btn0" onClick={onConfirmButtonClick.bind(this)}>{config.confirm}</a>}
                        {config.cancel && <a className="layui-layer-btn1" onClick={onCancelButtonClick.bind(this)}>{config.cancel}</a>}
					</div>}
                    {/*对话框resize的拖动条*/}
                    <span className="layui-layer-resize"></span>
                </div>
                {/*渲染对话框遮罩*/}
                <div className="layui-layer-shade" onClick={onCancelButtonClick.bind(this)}></div>
            </React.Fragment>;

            /** 对话框关闭回调*/
            function onCancelButtonClick()
            {
                // 用户回调
                config.onCancel && config.onCancel.call(this);
                // 关闭标识
                this.PageDialogState = undefined;
                this.PageDialogRef.current.className += ' layer-anim-close';
                window.setTimeout(() => this.refresh(), 600);
            }

            function onConfirmButtonClick()
            {
                // 用户回调
                config.onConfirm && config.onConfirm.call(this);
                // 关闭标识
                this.PageDialogState = undefined;
                this.PageDialogRef.current.className += ' layer-anim-close';
                window.setTimeout(() => this.refresh(), 600);
            }
        }
    }

    /**
     * 无条件刷新页面
     * 有的时候可能出现了用户用自己定义的数据结构渲染界面的情况
     * 此时只是用户数据结构变化，state没有变动，因此不会触发重新渲染
     * 此时用户通过调用此函数触发无条件渲染
     */
    public refresh(): void
    {
        /**
         * React特性:
         * 1.同步方法中调用多次setState只会触发1次render
         * 2.异步方法中调用多次setState会触发多次render
         * 为了避免在异步代码空间中用户多次赋值导致的多次渲染，只让第一次渲染有效
         */
        if (this.StateFlag)
        {
            this.StateFlag = false;
            setTimeout(() =>
            {
                /**
                 * React的setState({})方法无条件刷新页面且不会更改任何state数据
                 * 另外CGReact中原生数据state总是{}
                 * 在render()时，渲染页面前被赋值为state_original,渲染页面后赋值为state_original的代理对象
                 */
                super.setState({});
                // 重开刷新响应
                this.StateFlag = true;
            }, 100);
        }
    }

    /** 页面重定向到指定的地址*/
    public redirect(url: string, params?: { [key: string]: string }): void
    {
        this.props.history.push({
            pathname: url,
        })
    }

    /** 重新加载页面数据*/
    public reload(): void
    {
        // 重置加载标识
        this.MountOK = false;
        // state数据需要重新更新
        this.state_original = undefined;
        this.overrideState();
    }

    /**
     * 展示一个吐司
     * @param message
     * @param last
     */
    public toast(message: string | ToastState, level?: "info" | "warn" | "error" | "success", last?: number)
    {
        // 处理配置为undefined和string的情况
        message = message ? message : {};
        message = typeof message == "string" ? {message: message} : message;
        // 计算level
        level = level ? level : message.level ? message.level : "info";
        // 计算title
        let title = message.title ? message.title : undefined;
        title == undefined && level == "info" && (title = "Information");
        title == undefined && level == "warn" && (title = "Warning");
        title == undefined && level == "success" && (title = "Success");
        title == undefined && level == "error" && (title = "Error");
        // 保存并展示吐司
        this.PageToastState.push({
            level: level,
            title: title,
            message: message.message ? message.message : "这是一个CGReact吐司",
            last: last ? last : message.last ? message.last : 2600,
            proref: undefined,
            key: generateUUID()
        });
        this.refresh();
    }

    /*  展示一个对话框*/
    public dialog(message: ModalState | string)
    {
        // 处理undefined和string的情况
        message = message ? message : {};
        message = typeof message == "string" ? {content: message} : message;
        // 生成模态框配置
        let modal = {
            modal: true,
            show: true,
            title: message.title,
            content: typeof message.content == "string" ? <span>{message.content}</span> : message.content,
            cancel: message.cancel ? message.cancel == true ? "取消" : message.cancel : "取消",
            confirm: message.confirm ? message.confirm == true ? "确定" : message.confirm : undefined,
            onCancel: message.onCancel,
            onMount: message.onMount,
            onConfirm: message.onConfirm,
            onDismiss: message.onDismiss
        };
        modal.content = modal.content ? modal.content : CGReactApplication.makeCGReactLogo("CGReact对话框");
        // 保存并渲染
        this.PageDialogState = modal;
        this.refresh();
    }

    /**
     * 展示一个加载框
     * @param message
     * @param last
     */
    public loading(last?: number | true, type?: "dot" | "circle")
    {
        // 处理undefined的情况
        last = last == undefined ? 5000 : last;
        // 保存并显示loading...
        this.PageLoadingState = {
            last: last,
            type: type ? type : "circle"
        };
        this.refresh();
    }

    /** 取消显示loading对话框*/
    public dismiss(): void
    {
        // 淡出
        this.PageLoadingRef.current.className += ' layer-anim-close';
        // 600毫秒后干掉对话框
        this.PageLoadingState = null;
        window.setTimeout(() => this.refresh(), 600);
    }

    /**
     * 当state数据通过initState异步方法成功准备并返回的时候调用此方法
     * 对于普通组件此方法只在渲染时调用1次
     * 对于模态组件此方法会在每次渲染时调用
     */
    protected onPageStateReady(): void
    {
    }

    /**
     * 当界面数据完成初始化时调用此方法，在页面加载时调用一次，重新渲染不会再次调用
     * 注意这里和原生不太一样，由于initData方法是异步的，因此这里的调用要比原生的componentDidMount晚一些
     * 既异步数据初始化完成后，才调用这个方法，此时保障了this.state已经获得数据，并且调用一定在原生mount方法之后
     * 此时：state为代理对象
     */
    protected onPageDidMount(): void
    {
    }

    /**
     * 当界面重新渲染时调用此方法，每次render之后都会调用
     * 此时：state为代理对象
     */
    protected onPageDidUpdate(): void
    {
    }

    /** 当Page从页面卸载时时回调这个函数*/
    protected onPageWillUnMount(): void
    {
    }
}
